import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/api/client";

import { toOrder, type RawOrderListItem } from "../lib/order-list-vm";

/**
 * Fetches an order for the detail page. By default resolves by sale_order_id
 * (GET /v1/orders/by-sale-order-id/{sale_order_id}), which fans out to the
 * most-recently-created row when a marketplace split shares one
 * sale_order_id across several packages. When orderId is provided (set by a
 * RelatedOrdersBanner sibling link), fetches that EXACT package instead via
 * GET /v1/orders/{id} — bypassing the fan-out entirely so a specific sibling
 * is always reachable regardless of which one is "most recent".
 */
export function useOrder(saleOrderId: string, orderId?: string) {
  return useQuery({
    queryKey: orderId ? ["order", "by-id", orderId] : ["order", "by-sale-order-id", saleOrderId],
    enabled: !!orderId || !!saleOrderId,
    queryFn: async () => {
      if (orderId) {
        const { data, error } = await apiClient.GET("/v1/orders/{id}", {
          params: { path: { id: orderId } },
        });
        if (error) throw error;
        return toOrder(data as unknown as RawOrderListItem);
      }
      const { data, error } = await apiClient.GET("/v1/orders/by-sale-order-id/{sale_order_id}", {
        params: { path: { sale_order_id: saleOrderId } },
      });
      if (error) throw error;
      // BE returns a thinner projection than the detail page renders; MSW serves
      // the full view-model. toOrder() bridges both and defaults money/items so
      // totals(order.money) can't crash on a thin response (see order-list-vm).
      return toOrder(data as unknown as RawOrderListItem);
    },
  });
}
